// frontend/src/components/ContentInput/ContentInput.jsx

import React, { useContext, useRef, useEffect, useState, useMemo } from "react";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { AnalysisContext } from "../../contexts/AnalysisContext";
import { AuthContext } from "../../contexts/AuthContext";
import { UIContext } from "../../contexts/UIContext";
import "./ContentInput.css";
import {
    Tooltip, TextField, Button, CircularProgress, IconButton, Typography,
    Accordion, AccordionSummary, AccordionDetails, Box,
    Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, Checkbox, FormControlLabel,
    List, ListItem, ListItemButton
} from "@mui/material";
import Autocomplete from '@mui/material/Autocomplete';
import CancelIcon from '@mui/icons-material/Cancel';
import AudiotrackIcon from "@mui/icons-material/Audiotrack";
import VideocamIcon from "@mui/icons-material/Videocam";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import LinkIcon from "@mui/icons-material/Link";
import PublicIcon from '@mui/icons-material/Public';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import AddModeratorIcon from '@mui/icons-material/AddModerator';
import HistoryIcon from '@mui/icons-material/History';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import InfoIcon from '@mui/icons-material/Info';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import ReplayIcon from "@mui/icons-material/Replay";
import { getPlatformOptions, getPlatformDisplayName } from '../PlatformUtils/PlatformUtils.jsx';

const isMediaFileUrl = (url) => {
    if (!url) return false;
    // Check specifically for video extensions
    return /\.(mp4|webm|mov|avi|mkv|m4v)(\?.*)?$/i.test(url);
};

const isImageFileUrl = (url) => {
    if (!url) return false;
    // Check specifically for image extensions
    return /\.(jpg|jpeg|png|gif|webp|bmp|svg|tiff)(\?.*)?$/i.test(url);
};


// --- Check for text extensions ---
const isTextFileUrl = (url) => {
    if (!url) return false;
    return /\.(txt|md|csv|json|xml|log)(\?.*)?$/i.test(url);
};

// --- Check for audio extensions ---
const isAudioFileUrl = (url) => {
    if (!url) return false;
    return /\.(mp3|wav|ogg|aac|flac|m4a)(\?.*)?$/i.test(url);
};

function ContentInput({ onSubmit, isLoading }) {
    const { t } = useTranslation();
    const location = useLocation();
    const { setIsPageBlurred } = useContext(UIContext);
    const {
        contentType,
        setContentType,
        content,
        setContent,
        contentPreviewUrl,
        setContentPreviewUrl,
        setUploadProgress,
        setAnalysisResults,
        isUploading,
        tasks,
        setContentSourceUrl,
        setOriginalInputUrl,
        contextSourceUrl, setContextSourceUrl,
        contextPlatform, setContextPlatform,
        contextUserNotes, setContextUserNotes,
        estimatedCostUnits
    } = useContext(AnalysisContext);
    const { fetchWithAuth, user, isSessionLoading } = useContext(AuthContext);

    // --- TRIAL USAGE CHECKS ---
    const isMetered = !!user?.stripe_subscription_item_id;
    const isGuest = !!user?.is_guest;
    const parseDateSafe = (dateString, fallbackDaysAdd = 0) => {
        if (!dateString) return new Date(new Date().setDate(new Date().getDate() + fallbackDaysAdd));
        let parsed = new Date(dateString);
        if (!isNaN(parsed.getTime())) return parsed;
        if (typeof dateString === 'string') {
            const safeString = dateString.replace(/^(\d{4}-\d{2}-\d{2})\s(\d{2}:\d{2}:\d{2})/, '$1T$2');
            parsed = new Date(safeString);
            if (!isNaN(parsed.getTime())) return parsed;
        }
        return new Date();
    };

    const trialExpires = parseDateSafe(user?.trial_expires_at, 14);
    const daysRemaining = Math.max(0, Math.ceil((trialExpires - new Date()) / (1000 * 60 * 60 * 24)));
    const unitsRemaining = Math.max(0, Number(user?.compute_units_balance || 5000) - Number(user?.trial_spent || 0));

    const isTrial = !isGuest && !isMetered;
    const trialExhausted = unitsRemaining <= 0;
    const trialExpired = daysRemaining <= 0;
    const exceedsCredit = isTrial && (estimatedCostUnits > unitsRemaining);

    // --- CONSTANTS ---
    const MAX_TEXT_LENGTH = 25000;
    const MAX_URL_LENGTH = 500;
    const MAX_NOTES_LENGTH = 1000;

    const [imagePreview, setImagePreview] = useState(null);
    const [uploadedFileName, setUploadedFileName] = useState("");
    const [isDragging, setIsDragging] = useState(false);
    const [imageUrl, setImageUrl] = useState("");
    const [isFetchingUrl, setIsFetchingUrl] = useState(false);
    const [fetchError, setFetchError] = useState(null);
    const [fetchMessage, setFetchMessage] = useState(null);
    const [expanded, setExpanded] = useState(false);
    const [downloadProgress, setDownloadProgress] = useState(0);
    const [isProcessingServerSide, setIsProcessingServerSide] = useState(false);
    const [textDisplay, setTextDisplay] = useState("");    

    const fileInputRef = useRef(null);
    const dropZoneRef = useRef(null);
    const textFileRef = useRef(null);

    // =========================================================================
    // --- BROWSER EXTENSION: AUTO-POPULATE LOGIC ---
    // =========================================================================
    const [autoFetchUrl, setAutoFetchUrl] = useState(null);

    // 1. Intercept URL Parameters or Uploaded Files on Mount (Wait for Auth first!)
    useEffect(() => {
        // Wait until the user session is fully loaded before overriding the content type.
        // This prevents the user's default database preferences from reverting our extension's choice.
        if (isSessionLoading) return;

        // Check for direct File uploads transferred from Landing Page
        if (location.state?.autoFile) {
            const file = location.state.autoFile;
            const autoType = location.state.autoType || (file.type.startsWith('video/') ? 'video' : 'image');
            setContentType(autoType);
            setTimeout(() => {
                processFile(file, false, autoType);
                window.history.replaceState({}, document.title);
            }, 100);
            return;
        }

        const params = new URLSearchParams(window.location.search);
        const autoUrl = params.get('auto_analyze_url');
        const autoText = params.get('auto_analyze_text');
        const autoType = params.get('auto_analyze_type');

        if (autoUrl || autoText) {
            // Set the content type if provided
            if (autoType && ['image', 'video', 'audio', 'text'].includes(autoType)) {
                setContentType(autoType);
            }

            // Wait a tick for the contentType's cleanup effect to finish clearing states
            setTimeout(() => {
                if (autoUrl) {
                    setImageUrl(autoUrl);
                    setAutoFetchUrl(autoUrl);
                } else if (autoText) {
                    // --- Populate the textarea instantly! ---
                    setTextDisplay(autoText);
                    setContent(autoText);
                }
                
                // Clean the URL so a manual page refresh doesn't re-trigger it
                window.history.replaceState(null, '', window.location.pathname);
            }, 100);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isSessionLoading, setContentType, setContent]);

    // 2. Trigger the proxy fetch once the URL AND ContentType states are fully synced
    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const autoType = params.get('auto_analyze_type');
        
        // Ensure React has finished updating the contentType state before we fetch
        const typeIsReady = !autoType || contentType === autoType;

        if (autoFetchUrl && imageUrl === autoFetchUrl && typeIsReady) {
            setAutoFetchUrl(null); // Clear trigger
            handleFetchFileFromUrl(); 
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [imageUrl, autoFetchUrl, contentType]);
    // =========================================================================

    // Check if we have content (either file object or text string)
    // content can be a File object or a string.
    // If string, check length. If object, it exists.
    const hasContent = content && (typeof content !== 'string' || content.length > 0);

    // History Logic
    const [historyLoading, setHistoryLoading] = useState(false);
    const [contextHistory, setContextHistory] = useState([]);
    const [isHistoryModalOpen, setIsHistoryModalOpen] = useState(false);
    const [hasAttemptedHistoryFetch, setHasAttemptedHistoryFetch] = useState(false);

    // Consent Logic
    const [isConsentModalOpen, setIsConsentModalOpen] = useState(false);
    const [dontShowConsentAgain, setDontShowConsentAgain] = useState(false);

    // Source URL Validation
    const [sourceUrlError, setSourceUrlError] = useState(false);

    const platformOptions = useMemo(() => getPlatformOptions(t), [t]);

    // --- Helper to validate URL ---
    const isValidHttpUrl = (string) => {
        if (!string) return true; // Empty allowed

        // Regex for:
        // 1. Optional protocol (http:// or https://)
        // 2. Mandatory Domain name (e.g., google.com, www.test.co.uk)
        // 3. Optional port, path, query params
        const pattern = new RegExp(
            '^(https?:\\/\\/)?' + // protocol
            '((([a-z\\d]([a-z\\d-]*[a-z\\d])*)\\.)+[a-z]{2,}|' + // domain name
            '((\\d{1,3}\\.){3}\\d{1,3}))' + // OR ip (v4) address
            '(\\:\\d+)?(\\/[-a-z\\d%_.~+]*)*' + // port and path
            '(\\?[;&a-z\\d%_.~+=-]*)?' + // query string
            '(\\#[-a-z\\d_]*)?$', // fragment locator
            'i'
        );
        return !!pattern.test(string);
    };

    const handleResetContext = () => {
        setContextSourceUrl('');
        setContextPlatform('');
        setContextUserNotes('');
        setSourceUrlError(false);
    };

    const handleSourceUrlChange = (e) => {
        const val = e.target.value;
        if (val.length <= MAX_URL_LENGTH) {
            setContextSourceUrl(val);
            if (val.length > 0) {
                setSourceUrlError(!isValidHttpUrl(val));
            } else {
                setSourceUrlError(false);
            }
        }
    };

    // --- HELPER: Generate Label with Conditional Counter ---
    // This places the counter next to the label text, only if length > 0
    const getLabelWithCounter = (labelText, currentLength, maxLength) => {
        if (currentLength === 0) return labelText;

        return (
            <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                {labelText}
                <span style={{
                    opacity: 0.7,
                    fontSize: '0.9em',
                    color: currentLength >= maxLength ? '#ef5350' : 'inherit'
                }}>
                    ({currentLength}/{maxLength})
                </span>
            </span>
        );
    };

    // --- Effect to manage blur ---
    useEffect(() => {
        // Blur if EITHER modal is open
        const shouldBlur = isHistoryModalOpen || isConsentModalOpen;
        setIsPageBlurred(shouldBlur);

        // Cleanup on unmount to ensure blur is removed
        return () => setIsPageBlurred(false);
    }, [isHistoryModalOpen, isConsentModalOpen, setIsPageBlurred]);

    useEffect(() => {
        if (content instanceof File) {
            setUploadedFileName(content.name);
            if (contentType === "image") {
                const reader = new FileReader();
                reader.onloadend = () => {
                    setImagePreview(reader.result);
                };
                reader.readAsDataURL(content);
            }
        }
    }, [content, contentType]);

    const prevContentTypeRef = useRef(contentType);
    useEffect(() => {
        if (prevContentTypeRef.current !== contentType) {
            setContent('');
            setImagePreview(null);
            setUploadProgress(0);
            setUploadedFileName("");
            setAnalysisResults({});
            setImageUrl("");
            setFetchError(null);
            setExpanded(false);
            setSourceUrlError(false); // Reset validation error
            setHasAttemptedHistoryFetch(false);
            setContextHistory([]);
            if (fileInputRef.current) { fileInputRef.current.value = null; }
            if (textFileRef.current) { textFileRef.current.value = null; }
        }
        prevContentTypeRef.current = contentType;
    }, [contentType, setContent, setUploadProgress, setAnalysisResults]);

    // Sync content to textDisplay
    useEffect(() => {
        if (contentType === "text") {
            if (content instanceof File) {
                // If content is a File, read it for display only
                const reader = new FileReader();
                reader.onload = (e) => setTextDisplay(e.target.result);
                reader.readAsText(content);
            } else if (typeof content === "string") {
                // If content is already a string (user typed it), just show it
                setTextDisplay(content);
            } else {
                setTextDisplay("");
            }
        }
    }, [content, contentType]);

    const handleClearContent = () => {
        setContent(null);
        if (contentType === 'text') setContent('');
        setImagePreview(null);
        setUploadedFileName("");
        setContentSourceUrl("");
        setImageUrl("");
        setFetchError(null);
        setAnalysisResults({});
        setContextSourceUrl('');
        setContextPlatform('');
        setContextUserNotes('');
        setExpanded(false);
        setSourceUrlError(false);
        setHasAttemptedHistoryFetch(false);
        setContextHistory([]);
        if (fileInputRef.current) { fileInputRef.current.value = null; }
        if (textFileRef.current) { textFileRef.current.value = null; }
    };

    const processFile = (file, isFromUrl = false, explicitType = null) => {
        if (!file) return;

        // Use the explicitly provided type if available, otherwise fall back to React state
        const activeContentType = explicitType || contentType; 

        // Cleanup previous object URL to prevent memory leaks if it exists
        if (contentPreviewUrl && typeof contentPreviewUrl === 'string' && contentPreviewUrl.startsWith('blob:')) {
            URL.revokeObjectURL(contentPreviewUrl);
        }

        setContent(null);
        setImagePreview(null);
        setUploadProgress(0);
        setUploadedFileName("");
        setAnalysisResults({});
        setContentPreviewUrl(null);
        setFetchError(null);
        setHasAttemptedHistoryFetch(false); // Reset for new file
        setContextHistory([]);

        if (!isFromUrl) {
            setImageUrl("");
            setContentSourceUrl('');
        }

        if (fileInputRef.current) fileInputRef.current.value = null;
        if (textFileRef.current) textFileRef.current.value = null;

        const maxSizeMB = 50;
        const maxSizeBytes = maxSizeMB * 1024 * 1024;
        if (file.size > maxSizeBytes) {
            setFetchError(t("contentInput.errors.fileTooLarge", { maxSizeMB }));
            return;
        }

        if (activeContentType === "text") {
            if (!file.name.endsWith('.txt')) {
                setFetchError(t("contentInput.errors.invalidFileType.text"));
                return;
            }
            
            // Don't read as string here. Just set the File object.
            // This ensures 'content' remains a File, so the browser sends the filename.
            setContent(file);
            setUploadedFileName(file.name);
            return;
        }

        let allowedTypes = [];
        let alertMessage = t("contentInput.errors.invalidFileType.general");
        if (activeContentType === "image") {
            allowedTypes = ["image/jpeg", "image/png", "image/gif", "image/webp"];
            alertMessage = t("contentInput.errors.invalidFileType.image");
        } else if (activeContentType === "audio") {
            allowedTypes = ["audio/mpeg", "audio/mp3", "audio/wav", "audio/ogg", "audio/aac", "audio/flac", "audio/mp4", "audio/x-m4a"];
            alertMessage = t("contentInput.errors.invalidFileType.audio");
        } else if (activeContentType === "video") {
            allowedTypes = ["video/mp4", "video/webm", "video/ogg", "video/quicktime", "video/x-msvideo", "video/avi", "video/x-matroska"];
            alertMessage = t("contentInput.errors.invalidFileType.video");
        }

        const isFromYoutube = isFromUrl && (imageUrl.includes('youtube.com') || imageUrl.includes('youtu.be'));
        if (!isFromUrl && !isFromYoutube && allowedTypes.length > 0 && !allowedTypes.includes(file.type)) {
            setFetchError(`${alertMessage} (Selected type: ${file.type || "unknown"})`);
            return;
        }

        setContent(file);
        setUploadedFileName(file.name);

        if (activeContentType === "image") {
            const reader = new FileReader();
            reader.onloadend = () => {
                const result = reader.result;
                setContentPreviewUrl(result);
                setImagePreview(result);
            };
            reader.readAsDataURL(file);
        } else if (activeContentType === "video" || activeContentType === "audio") {
            // Use createObjectURL for media files (much better performance than Base64)
            const objectUrl = URL.createObjectURL(file);
            setContentPreviewUrl(objectUrl);
            setImagePreview(null);
        }
    };

    const isSocialUrl = (url) => {
        return /youtube\.com|youtu\.be|tiktok\.com|instagram\.com|x\.com|x\.com|meta\.com/i.test(url);
    };

    const handleFetchFileFromUrl = async () => {
        if (!imageUrl.trim()) {
            setFetchError(t('contentInput.errors.urlRequired'));
            return;
        }
        setOriginalInputUrl(imageUrl.trim());
        setIsFetchingUrl(true);
        setFetchError(null);
        setFetchMessage(null);

        const downloadAndProcessMedia = async (finalMediaUrl, sourceType = 'direct') => {
            // 1. Set Message & State
            // We check isSocialUrl OR if the content type implies a large file that needs processing
            if (isSocialUrl(finalMediaUrl) || contentType === 'video' || contentType === 'audio') {
                if (isSocialUrl(finalMediaUrl)) {
                    setFetchMessage("Processing video on server... (This may take a moment)");
                } else {
                    setFetchMessage(t('contentInput.fetching.largeFileMessage'));
                }
                // Force the bar to show immediately
                setIsProcessingServerSide(true);
            }

            setDownloadProgress(0);

            try {
                // This await will hang here while the server processes the YouTube video
                const response = await fetchWithAuth("/api/proxy/file", {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({ url: finalMediaUrl, contentType: contentType }),
                });

                // 2. Server is done! Turn off processing mode, start download mode.
                setIsProcessingServerSide(false);

                if (!response.ok) {
                    const errorData = await response.json();
                    throw new Error(errorData.error || `Failed to fetch from URL (status: ${response.status})`);
                }

                setFetchMessage("Transferring to browser...");

                const contentLength = response.headers.get('Content-Length');
                const total = contentLength ? parseInt(contentLength, 10) : null;
                const reader = response.body.getReader();

                let receivedLength = 0;
                let chunks = [];

                while (true) {
                    const { done, value } = await reader.read();
                    if (done) break;

                    chunks.push(value);
                    receivedLength += value.length;

                    let lastPercent = 0;

                    if (total) {
                        const percent = Math.round((receivedLength / total) * 100);
                        // Only trigger a React re-render if the percentage integer changed
                        if (percent !== lastPercent) {
                            setDownloadProgress(percent);
                            lastPercent = percent;
                        }
                    }

                }

                const blob = new Blob(chunks);


                // --- POST-DOWNLOAD VALIDATION: Check MIME type of the blob ---
                const actualType = blob.type.toLowerCase();
                
                if (contentType === 'image' && (actualType.startsWith('text/') || actualType.startsWith('audio/') || actualType.startsWith('video/'))) {
                    throw new Error(t("contentInput.errors.invalidFileType.image"));
                }
                if (contentType === 'video' && (actualType.startsWith('text/') || actualType.startsWith('image/') || actualType.startsWith('audio/'))) {
                    throw new Error(t("contentInput.errors.invalidFileType.video"));
                }
                if (contentType === 'audio' && (actualType.startsWith('text/') || actualType.startsWith('image/') || actualType.startsWith('video/'))) {
                    throw new Error(t("contentInput.errors.invalidFileType.audio"));
                }
                // -------------------------------------------------------------

                const disposition = response.headers.get('Content-Disposition');
                let filename;

                if (sourceType === 'dcm_image') {
                    filename = 'dcm_creative.jpg';
                } else if (sourceType === 'dcm_video') {
                    filename = 'dcm_video.mp4';
                } else if (disposition && disposition.includes('filename=')) {
                    const filenameMatch = disposition.match(/filename="([^"]+)"/);
                    if (filenameMatch && filenameMatch[1]) {
                        filename = filenameMatch[1];
                    }
                }

                if (!filename) {
                    try {
                        const urlPath = new URL(finalMediaUrl).pathname;
                        filename = urlPath.substring(urlPath.lastIndexOf('/') + 1) || 'downloaded_file';
                    } catch (e) {
                        filename = 'downloaded_file';
                    }
                }

                let mimeType = blob.type;
                if (!mimeType || mimeType === 'application/octet-stream') {
                    if (contentType === 'video') mimeType = 'video/mp4';
                    if (contentType === 'audio') mimeType = 'audio/mpeg';
                    if (contentType === 'image') mimeType = 'image/jpeg';
                }

                // --- FORCE EXTENSION IF MISSING ---
                // Many dynamic URLs (like Google Images) yield filenames without extensions (e.g., 'images'). 
                // This causes the backend validation to fail. We force an extension based on the content type.
                if (filename && !filename.includes('.')) {
                    if (contentType === 'image') filename += '.jpg';
                    else if (contentType === 'video') filename += '.mp4';
                    else if (contentType === 'audio') filename += '.mp3';
                    else if (contentType === 'text') filename += '.txt';
                }

                const fetchedFile = new File([blob], filename, { type: mimeType });
                setContentSourceUrl(finalMediaUrl);
                processFile(fetchedFile, true);

                setDownloadProgress(0);
            } catch (error) {
                // Ensure state is reset on error
                setIsProcessingServerSide(false);
                throw error;
            }
        };

        // ... (Rest of your try/catch logic for DCM/VAST remains exactly the same) ...
        try {
            const trimmedInput = imageUrl.trim();
            // 0. Block local browser Blob buffers instantly
            if (trimmedInput.startsWith('blob:')) {
                throw new Error("Blob URLs are temporary browser buffers and cannot be processed. Please provide the actual public page URL.");
            }
            const isSocial = isSocialUrl(trimmedInput);
            const isDirectVideoUrl = isMediaFileUrl(trimmedInput);
            const isDirectImageUrl = isImageFileUrl(trimmedInput);
            const isDirectTextUrl = isTextFileUrl(trimmedInput);
            const isDirectAudioUrl = isAudioFileUrl(trimmedInput);

            // 1. Block Social Media links if Content Type is NOT Video/Audio
            if (isSocial && contentType !== 'video' && contentType !== 'audio') {
                throw new Error(t("contentInput.errors.socialMediaMismatch"));
            }

            // 2. Block Direct Video files if Content Type is NOT Video
            if (isDirectVideoUrl && contentType !== 'video') {
                throw new Error(t("contentInput.errors.videoForVideoOnly"));
            }

            // 3. Block Direct Image files if Content Type is NOT Image
            if (isDirectImageUrl && contentType !== 'image') {
                throw new Error(t("contentInput.errors.imageForImageOnly"));
            }


            // 4. Block Direct Text files if Content Type is NOT Text
            if (isDirectTextUrl && contentType !== 'text') {
                // Use specific error messages for better UX
                if (contentType === 'image') throw new Error(t("contentInput.errors.invalidFileType.image"));
                if (contentType === 'video') throw new Error(t("contentInput.errors.invalidFileType.video"));
                if (contentType === 'audio') throw new Error(t("contentInput.errors.invalidFileType.audio"));
            }

            // 5. Block Direct Audio files if Content Type is NOT Audio
            if (isDirectAudioUrl && contentType !== 'audio') {
                throw new Error(t("contentInput.errors.invalidFileType.audio", { defaultValue: "Please select 'Audio' to process audio files." }));
            }

            // ... (Keep your DCM / VAST Logic here) ...
            // Just ensure they call `downloadAndProcessMedia` correctly
            const isDcmJsTag = trimmedInput.startsWith("<ins class='dcmads'");
            const isVastTag = trimmedInput.includes('/pfadx/') || trimmedInput.endsWith('.xml');

            if (isDcmJsTag) {
                setFetchMessage("Rendering DCM display tag...");
                const renderResponse = await fetchWithAuth('/api/bulk/render-dcm-tag', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ ad_tag: trimmedInput })
                });
                const renderData = await renderResponse.json();
                if (!renderResponse.ok) throw new Error(renderData.error || t("contentInput.errors.dcmRenderFailed"));
                const finalMediaUrl = renderData.imageUrl;
                if (finalMediaUrl) await downloadAndProcessMedia(finalMediaUrl, 'dcm_image');
                else throw new Error(t("contentInput.errors.noMediaUrlFromTag"));

            } else if (isVastTag) {
                // ... existing VAST logic ...
                if (contentType !== 'video') throw new Error(t("contentInput.errors.vastForVideoOnly"));
                setFetchMessage("Resolving VAST video tag...");
                const resolveResponse = await fetchWithAuth('/api/video/resolve-vast', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ vast_url: trimmedInput })
                });
                const resolveData = await resolveResponse.json();
                if (!resolveResponse.ok) throw new Error(resolveData.error || t("contentInput.errors.vastResolveFailed"));
                const finalMediaUrl = resolveData.videoUrl;
                if (finalMediaUrl) await downloadAndProcessMedia(finalMediaUrl, 'dcm_video');
                else throw new Error("Could not determine a valid media URL from the VAST tag.");

            } else {
                // Standard URL
                await downloadAndProcessMedia(trimmedInput, 'direct');
            }
        } catch (err) {
            setFetchError(err.message);
            console.error("URL Fetch/Resolve error:", err);
        } finally {
            setIsFetchingUrl(false);
            setFetchMessage(null);
        }
    };

    const handleTextChange = (event) => {
        const val = event.target.value;
        // Enforce max length
        if (val.length <= MAX_TEXT_LENGTH) {
            setTextDisplay(val); // Update display
            setContent(val);     // Update global context
            setHasAttemptedHistoryFetch(false);
            if (uploadedFileName) {
                setUploadedFileName("");
            }
        }
    };

    // --- Handle Analyze Click with Interception ---
    const handleAnalyzeClick = () => {
        // 1. Validate URL before doing anything else
        if (contextSourceUrl && !isValidHttpUrl(contextSourceUrl)) {
            setSourceUrlError(true);
            return;
        }

        // 2. Check if user added ANY context (Note OR URL)
        // Both of these fields appear on the public verification page, so both require consent.
        const hasSensitiveContext = (contextUserNotes && contextUserNotes.trim().length > 0) ||
            (contextSourceUrl && contextSourceUrl.trim().length > 0);

        if (hasSensitiveContext) {
            const sessionConsent = sessionStorage.getItem('auth_ai_public_consent');
            if (sessionConsent === 'true') {
                onSubmit();
            } else {
                setIsConsentModalOpen(true);
            }
        } else {
            onSubmit();
        }
    };

    const handleConsentConfirm = () => {
        if (dontShowConsentAgain) {
            sessionStorage.setItem('auth_ai_public_consent', 'true');
        }
        setIsConsentModalOpen(false);
        onSubmit();
    };

    const handleDeleteNoteAndProceed = () => {
        // 1. Update the UI state so the fields look empty to the user immediately
        setContextUserNotes('');
        setContextSourceUrl('');
        
        // 2. Close modal
        setIsConsentModalOpen(false);

        // 3. Trigger submit with EXPLICIT empty strings. 
        // This bypasses the race condition by forcing AppContent to use these values 
        // instead of waiting for the Context state to update.
        onSubmit({
            contextUserNotes: '',
            contextSourceUrl: ''
        });
    };

    // --- Fetch Context History (Silent) ---
    const fetchContextHistory = async () => {
        if (!content || hasAttemptedHistoryFetch) return;

        setHistoryLoading(true);
        try {
            const formData = new FormData();
            formData.append("type", contentType);

            if (contentType === 'text') {
                formData.append("content", content);
            } else {
                formData.append("content", content);
            }

            const response = await fetchWithAuth('/api/analysis/context-history', {
                method: 'POST',
                body: formData
            });

            if (response.ok) {
                const data = await response.json();
                setContextHistory(data.history || []);
            }
        } catch (err) {
            console.error("Failed to fetch history", err);
        } finally {
            setHistoryLoading(false);
            setHasAttemptedHistoryFetch(true); // Mark as fetched
        }
    };

    // --- Handle Accordion Expansion ---
    const handleAccordionChange = (event, isExpanded) => {
        setExpanded(isExpanded);
        if (isExpanded) {
            fetchContextHistory();
        }
    };

    // --- Open Modal Handler ---
    const handleOpenHistoryModal = (e) => {
        e.stopPropagation(); // Prevent accordion collapse
        setIsHistoryModalOpen(true);
    };

    const applyContextFromHistory = (historyItem) => {
        // Always set the state, using '' if the value is null/undefined.
        // This ensures previous values are overwritten/cleared correctly.

        const url = historyItem.source_url || '';
        setContextSourceUrl(url);
        // Only validate if there is actually a URL, otherwise clear error
        if (url) {
            setSourceUrlError(!isValidHttpUrl(url));
        } else {
            setSourceUrlError(false);
        }

        setContextPlatform((historyItem.platform || '').toLowerCase());
        setContextUserNotes(historyItem.user_notes || '');

        setIsHistoryModalOpen(false);
    };

    const handleFileChange = (event) => processFile(event.target.files[0]);
    const handleDragEnter = (event) => { event.preventDefault(); event.stopPropagation(); setIsDragging(true); };
    const handleDragOver = (event) => { event.preventDefault(); event.stopPropagation(); setIsDragging(true); };
    const handleDragLeave = (event) => { event.preventDefault(); event.stopPropagation(); if (!dropZoneRef.current?.contains(event.relatedTarget)) setIsDragging(false); };
    const handleDrop = (event) => {
        event.preventDefault();
        event.stopPropagation();
        setIsDragging(false);
        if (event.dataTransfer.files && event.dataTransfer.files.length > 0) {
            processFile(event.dataTransfer.files[0]);
            event.dataTransfer.clearData();
        }
    };
    const handleTextDrop = (event) => {
        event.preventDefault();
        event.stopPropagation();
        setIsDragging(false);
        if (event.dataTransfer.files && event.dataTransfer.files[0]) {
            processFile(event.dataTransfer.files[0]);
        }
    };

    const isInputMissing = !content || (typeof content === 'string' && !content.trim());
    const areTasksMissing = tasks.length === 0;

    // --- MODIFIED BUTTON STATE LOGIC ---
    const isButtonDisabled = isLoading || isInputMissing || areTasksMissing || sourceUrlError || (isTrial && (trialExhausted || trialExpired || exceedsCredit));

    const getTooltipTitle = () => {
        if (isLoading) return "";
        const errorMessages = [];

        // Trial Warning Errors (ONLY apply if the user is on a trial)
        if (isTrial) {
            if (trialExpired) {
                errorMessages.push(t('contentInput.tooltips.trialExpired'));
            } else if (trialExhausted) {
                errorMessages.push(t('contentInput.tooltips.trialExhausted'));
            } else if (exceedsCredit) {
                errorMessages.push(t('contentInput.tooltips.trialInsufficient'));
            }
        }

        const translatedContentTypeForTooltip = t(`contentInput.contentTypes.${contentType}`, {
            defaultValue: contentType.charAt(0).toUpperCase() + contentType.slice(1)
        });
        if (isInputMissing) {
            const key = contentType === 'text' ? 'contentInput.tooltips.enterText' : 'contentInput.tooltips.uploadFile';
            errorMessages.push(t(key, { contentType: translatedContentTypeForTooltip }));
        }
        if (areTasksMissing) {
            errorMessages.push(t('contentInput.tooltips.selectTask'));
        }
        // --- TOOLTIP LOGIC ---
        if (sourceUrlError) {
            errorMessages.push(t('contentInput.tooltips.fixSourceUrl'));
        }

        if (errorMessages.length === 0) return "";
        
        // If there's only one error, show it plainly
        if (errorMessages.length === 1) return errorMessages[0];
        
        // If there are multiple errors, show them as a clean list
        return (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', padding: '4px 0' }}>
                {errorMessages.map((msg, idx) => (
                    <span key={idx}>• {msg}</span>
                ))}
            </div>
        );
    };
    const tooltipTitle = getTooltipTitle();
    const translatedContentType = t(`contentInput.contentTypes.${contentType}`);
    let buttonText;
    if (isUploading) {
        buttonText = t("contentInput.buttons.uploading", { contentType: translatedContentType });
    } else if (isLoading) {
        buttonText = t("contentInput.buttons.analyzing");
    } else {
        buttonText = t("contentInput.buttons.analyze", { contentType: translatedContentType });
    }

    const getTaskMode = () => {
        const hasAi = tasks.includes('ai_detection');
        const hasFakeNews = tasks.includes('fake_news_detection');

        if (hasAi && hasFakeNews) return 'both';
        if (hasAi) return 'ai';
        if (hasFakeNews) return 'fake_news';
        return 'both'; // Default fallback
    };

    // --- Helper to get dynamic placeholder ---
    const getNotesPlaceholder = () => {
        const taskMode = getTaskMode();
        // Construct key: contentInput.context.userNotesPlaceholders.image.ai
        const key = `contentInput.context.userNotesPlaceholders.${contentType}.${taskMode}`;

        // Check if translation exists, otherwise fallback to default
        const placeholder = t(key, { defaultValue: null });
        if (placeholder) return placeholder;

        return t('contentInput.context.userNotesPlaceholders.default');
    };

    // --- Helper to get dynamic tooltip for the "Why" icon ---
    const getContextWhyTooltip = () => {
        const taskMode = getTaskMode();
        // Construct key: contentInput.context.contextWhyTooltips.video.fake_news
        const key = `contentInput.context.contextWhyTooltips.${contentType}.${taskMode}`;

        // Check if translation exists, otherwise fallback
        const tooltip = t(key, { defaultValue: null });
        if (tooltip) return tooltip;

        // Fallback if the specific combo isn't found, try to find a generic one or hard default
        return t('contentInput.context.contextWhyTooltips.default', {
            defaultValue: "Providing context helps the AI differentiate between content that is technically manipulated versus content that is deceptively manipulated."
        });
    };

    // --- Helper to render the Context UI ---
    const renderContextAccordion = () => (
        <Accordion
            expanded={expanded}
            onChange={handleAccordionChange}
            className="context-accordion connected-top"
            disableGutters
        >
            <AccordionSummary expandIcon={<ExpandMoreIcon className="context-expand-icon" />}>
                <div className="context-accordion-header" style={{ display: 'flex', alignItems: 'center', width: '100%', paddingRight: '10px' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px', flexGrow: 1 }}>
                        <AddModeratorIcon fontSize="small" />
                        <Typography variant="body1" style={{ fontWeight: 500 }}>
                            {t('contentInput.context.accordionTitle')}
                        </Typography>
                        <Tooltip
                            title={t('contentInput.context.accordionInfoTooltip')}
                            arrow
                            placement="top"
                            PopperProps={{ className: 'global-dark-tooltip' }}
                        >
                            {/* stopPropagation prevents the accordion from toggling when clicking the info icon */}
                            <IconButton size="small" onClick={(e) => e.stopPropagation()} sx={{ padding: 0, color: 'rgba(255,255,255,0.5)', '&:hover': { color: 'white' } }}>
                                <InfoIcon fontSize="small" sx={{ color: 'rgba(255, 255, 255, 0.7)' }} />
                            </IconButton>
                        </Tooltip>
                    </Box>

                    {/* History Button inside Header */}
                    {content && (
                        <Box onClick={(e) => e.stopPropagation()} sx={{ display: 'flex', alignItems: 'center' }}>
                            {historyLoading ? (
                                <CircularProgress size={16} sx={{ color: 'rgba(255,255,255,0.5)', mr: 1 }} />
                            ) : contextHistory.length > 0 && (
                                <Button
                                    size="small"
                                    startIcon={<HistoryIcon />}
                                    onClick={handleOpenHistoryModal}
                                    className="context-history-btn"
                                    disabled={isLoading}
                                    sx={{
                                        borderColor: 'rgba(255, 255, 255, 0.5)',
                                        color: 'rgba(255, 255, 255, 0.9)',
                                        textTransform: 'none',
                                        fontSize: '0.75rem',
                                        height: '28px',
                                        minWidth: 'auto',
                                        whiteSpace: 'nowrap',
                                        '&:hover': {
                                            borderColor: 'var(--secondary-color)',
                                            backgroundColor: 'rgba(79, 195, 247, 0.1)',
                                            color: 'var(--secondary-color)',
                                        }
                                    }}
                                    variant="outlined"
                                >
                                    {t('contentInput.context.historyButton')} ({contextHistory.length})
                                    <Tooltip
                                        title={t('contentInput.context.historyButtonInfoTooltip')}
                                        arrow
                                        placement="top"
                                        PopperProps={{ className: 'global-dark-tooltip' }}
                                    >
                                        <Box component="span" sx={{ display: 'flex', alignItems: 'center', ml: 1 }}>
                                            <InfoIcon sx={{ fontSize: '1rem', color: 'rgba(255, 255, 255, 0.7)' }} />
                                        </Box>
                                    </Tooltip>
                                </Button>
                            )}
                        </Box>
                    )}
                </div>
            </AccordionSummary>
            <AccordionDetails sx={{ position: 'relative' }}>

                {/* Floating Reset Button */}
                {(contextSourceUrl || contextPlatform || contextUserNotes) && (
                    <Tooltip title={t('contentInput.context.resetTooltip')}>
                        <IconButton
                            size="small"
                            onClick={handleResetContext}
                            disabled={isLoading}
                            sx={{
                                position: 'absolute',
                                top: '-68px', // Floats half-in/half-out of the header line
                                right: '-15px',
                                backgroundColor: 'var(--secondary-color)',
                                color: 'white',
                                boxShadow: '0 2px 5px rgba(0,0,0,0.2)',
                                width: '30px',
                                height: '30px',
                                zIndex: 2,
                                transition: 'all 0.2s ease-in-out',
                                '&:hover': {
                                    backgroundColor: 'var(--btn-hover-color)',
                                    transform: 'scale(1.1)',
                                    boxShadow: '0 4px 8px rgba(0,0,0,0.3)',
                                }
                            }}
                        >
                            <ReplayIcon />
                        </IconButton>
                    </Tooltip>
                )}

                <Typography variant="caption" color="textSecondary" className="context-subtitle" display="block" gutterBottom>
                    {t('contentInput.context.accordionSubtitle')}
                </Typography>

                <div className="context-inputs-grid">
                    {/* Source URL */}
                    <Tooltip
                        title={contextSourceUrl || ""}
                        arrow
                        disableHoverListener={!contextSourceUrl}
                        placement="top"
                    >
                        <TextField
                            // Use the helper to inject counter into the label
                            label={getLabelWithCounter(
                                t('contentInput.context.sourceUrlLabel'),
                                contextSourceUrl.length,
                                MAX_URL_LENGTH
                            )}
                            placeholder={t('contentInput.context.sourceUrlPlaceholder')}
                            variant="outlined"
                            size="small"
                            fullWidth
                            value={contextSourceUrl}
                            onChange={handleSourceUrlChange}
                            className="context-text-field"
                            disabled={isLoading}
                            error={sourceUrlError}
                            // Helper text is now ONLY for the error message
                            helperText={sourceUrlError ? t('contentInput.context.sourceUrlError') : null}
                            inputProps={{
                                maxLength: MAX_URL_LENGTH
                            }}
                            InputProps={{
                                startAdornment: !contextSourceUrl && (
                                    <LinkIcon fontSize="small" sx={{ color: 'rgba(255, 255, 255, 0.5)', mr: 1 }} />
                                ),
                                sx: {
                                    '& input': {
                                        textOverflow: 'ellipsis',
                                    }
                                }
                            }}
                        />
                    </Tooltip>

                    {/* Platform Select */}
                    <Autocomplete
                        id="platform-autocomplete"
                        options={platformOptions}
                        getOptionLabel={(option) => option.label}
                        value={contextPlatform ? (platformOptions.find(opt => opt.value === contextPlatform.toLowerCase()) || null) : null}
                        onChange={(event, newValue) => {
                            setContextPlatform(newValue ? newValue.value : '');
                        }}
                        disabled={isLoading}
                        fullWidth
                        size="small"
                        // REMOVED: disableClearable (Restores the X button)

                        // 1. White Dropdown Styling
                        ListboxProps={{
                            sx: {
                                backgroundColor: 'white',
                                color: 'rgba(0, 0, 0, 0.87)',
                                '& .MuiAutocomplete-option': {
                                    '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' },
                                    '&[aria-selected="true"]': { backgroundColor: 'rgba(25, 118, 210, 0.08)' },
                                    '&[aria-selected="true"]:hover': { backgroundColor: 'rgba(25, 118, 210, 0.12)' }
                                }
                            }
                        }}

                        // 2. Option Rendering
                        renderOption={(props, option) => {
                            const { key, ...restProps } = props;
                            return (
                                <Box
                                    component="li"
                                    key={key}
                                    {...restProps}
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 1.5,
                                        color: option.value === '' ? 'rgba(0, 0, 0, 0.38)' : 'inherit',
                                        fontStyle: option.value === '' ? 'italic' : 'normal'
                                    }}
                                >
                                    <Box sx={{ color: 'rgba(0, 0, 0, 0.54)', display: 'flex' }}>
                                        {option.icon}
                                    </Box>
                                    {option.label}
                                </Box>
                            );
                        }}

                        // 3. Input Rendering
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label={t('contentInput.context.platformLabel')}
                                placeholder={t('contentInput.context.platformPlaceholder')}
                                className="context-text-field"
                                variant="outlined"
                                InputProps={{
                                    ...params.InputProps,
                                    startAdornment: (
                                        <>
                                            <Box
                                                component="span"
                                                sx={{
                                                    color: isLoading
                                                        ? 'rgba(255, 255, 255, 0.3)'
                                                        : (contextPlatform ? 'white' : 'rgba(255, 255, 255, 0.4)'),
                                                    mr: 1,
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    pl: 0.5
                                                }}
                                            >
                                                {contextPlatform
                                                    ? platformOptions.find(o => o.value === contextPlatform.toLowerCase())?.icon
                                                    : <PublicIcon fontSize="small" />
                                                }
                                            </Box>
                                            {params.InputProps.startAdornment}
                                        </>
                                    )
                                }}
                                sx={{
                                    '& .MuiOutlinedInput-input': {
                                        // If contextPlatform has value, text is white.
                                        // If empty (placeholder showing), this color doesn't matter as placeholder style takes over, 
                                        // BUT we ensure placeholder is dimmed.
                                        color: contextPlatform ? 'white' : 'rgba(255, 255, 255, 0.4) !important',
                                        '&::placeholder': {
                                            color: 'rgba(255, 255, 255, 0.4)',
                                            opacity: 1,
                                        }
                                    }
                                }}
                            />
                        )}

                        // 4. General Styling
                        sx={{
                            '& .MuiOutlinedInput-root': {
                                paddingLeft: '8px !important',
                            },
                            '& .MuiAutocomplete-popupIndicator': {
                                color: 'rgba(255, 255, 255, 0.7)'
                            },
                            '& .MuiAutocomplete-clearIndicator': {
                                color: 'rgba(255, 255, 255, 0.7)' // Ensure X is visible
                            }
                        }}
                    />

                    {/* User Notes */}
                    <TextField
                        // Use the helper to inject counter into the label
                        label={getLabelWithCounter(
                            t('contentInput.context.userNotesLabel'),
                            contextUserNotes.length,
                            MAX_NOTES_LENGTH
                        )}
                        placeholder={getNotesPlaceholder()}
                        variant="outlined"
                        size="small"
                        multiline
                        minRows={2}
                        fullWidth
                        value={contextUserNotes}
                        onChange={(e) => setContextUserNotes(e.target.value)}
                        className="context-text-field full-width"
                        disabled={isLoading}
                        inputProps={{
                            maxLength: MAX_NOTES_LENGTH
                        }}
                        // Helper text removed entirely to save space
                        helperText={null}
                        InputProps={{
                            startAdornment: !contextUserNotes && (
                                <Tooltip
                                    title={getContextWhyTooltip()}
                                    arrow
                                    placement="top"
                                    PopperProps={{ className: 'global-dark-tooltip' }}
                                >
                                    <InfoOutlinedIcon
                                        fontSize="small"
                                        sx={{
                                            color: 'rgba(255, 255, 255, 0.5)',
                                            mr: 1,
                                            alignSelf: 'flex-start',
                                            cursor: 'help'
                                        }}
                                    />
                                </Tooltip>
                            ),
                        }}
                    />
                </div>
            </AccordionDetails>
        </Accordion>
    );

    const renderFileInput = () => {
        const fileTypeIcons = {
            audio: <AudiotrackIcon className="file-preview-icon" />,
            video: <VideocamIcon className="file-preview-icon" />,
        };

        const isPlayableVideo = content instanceof File && (
            ['video/mp4', 'video/webm', 'video/ogg', 'video/quicktime'].includes(content.type) ||
            /\.(mp4|webm|ogg|mov)$/i.test(content.name)
        );

        return (
            <div className="file-input-area">
                <input type="file" id="content" accept={`${contentType}/*`} onChange={handleFileChange} ref={fileInputRef} style={{ display: "none" }} aria-hidden="true" />
                <div
                    ref={dropZoneRef}
                    className={`drop-zone ${isDragging ? "dragging" : ""} ${hasContent ? 'has-content connected-bottom' : ''}`}
                    onDragEnter={handleDragEnter} 
                    onDragOver={handleDragOver} 
                    onDragLeave={handleDragLeave} 
                    onDrop={handleDrop}
                    onClick={() => !content && fileInputRef.current?.click()}
                    // role="button" 
                    // tabIndex={0} 
                    // aria-label={t("contentInput.aria.uploadFile", { translatedContentType })}
                    onKeyDown={(e) => { if (!content && (e.key === "Enter" || e.key === " ")) fileInputRef.current?.click(); }}
                >

                    {content && (
                        <Tooltip title={t("contentInput.tooltips.clearSelection")}>
                            <IconButton
                                size="small"
                                className="clear-content-button"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    handleClearContent();
                                }}
                                aria-label={t("contentInput.tooltips.clearFileAria")}
                            >
                                <CancelIcon />
                            </IconButton>
                        </Tooltip>
                    )}

                    {!content ? (
                        isFetchingUrl ? (
                            // --- THIS IS THE NEW LOADING STATE UI ---
                            <div className="url-fetching-state">
                                <CircularProgress color="secondary" />
                                {fetchMessage && (
                                    <Typography variant="body2" className="fetch-message-text">
                                        {fetchMessage}
                                    </Typography>
                                )}

                                {/* Show bar if downloading OR if processing on server */}
                                {(downloadProgress > 0 || isProcessingServerSide) && (
                                    <div className="progress-bar-container" style={{ maxWidth: '300px', marginTop: '10px' }}>
                                        <div
                                            className={`progress-bar ${isProcessingServerSide ? 'indeterminate' : ''}`}
                                            style={{ width: isProcessingServerSide ? '100%' : `${downloadProgress}%` }}
                                        ></div>

                                        {/* 
                                        Update the text logic:
                                        If processing -> Show "Processing..." inside the bar area
                                        If downloading -> Show "XX%" 
                                        */}
                                        <span className="progress-text">
                                            {isProcessingServerSide ? "Processing..." : `${downloadProgress}%`}
                                        </span>
                                    </div>
                                )}
                            </div>
                        ) : (
                            // --- This is the original placeholder UI ---
                            <div className="drop-zone-placeholder-wrapper">
                                <div className="drop-zone-placeholder">
                                    <UploadFileIcon className="drop-zone-icon" />
                                    <p className="drop-zone-instructions">
                                        {t("contentInput.dropZone.dragAndDrop", { translatedContentType })}{" "}
                                        <span 
                                            className="upload-link" 
                                            role="button" 
                                            tabIndex={0} 
                                            onClick={(e) => { e.stopPropagation(); fileInputRef.current?.click(); }} 
                                            onKeyDown={(e) => { if(e.key === 'Enter' || e.key === ' ') { e.stopPropagation(); fileInputRef.current?.click(); } }}
                                        >
                                            {t("contentInput.dropZone.uploadLink")}
                                        </span>
                                    </p>
                                </div>
                                {(contentType === 'image' || contentType === "video" || contentType === "audio") && (
                                    <>
                                        <div className="or-separator">{t("authPage.guest.separator")}</div>
                                        <div className="url-input-container" onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()}>
                                            <TextField
                                                fullWidth variant="outlined"
                                                label={t("contentInput.dropZone.urlPlaceholder", { translatedContentType })}
                                                value={imageUrl} onChange={(e) => setImageUrl(e.target.value)}
                                                disabled={isFetchingUrl || isLoading} error={!!fetchError} helperText={fetchError}
                                                InputProps={{ startAdornment: (<LinkIcon sx={{ color: "action.active", mr: 1, my: 0.5 }} />), }}
                                            />
                                            <Button variant="contained" onClick={handleFetchFileFromUrl} disabled={isFetchingUrl || isLoading || !imageUrl} className="search-button">
                                                {isFetchingUrl ? (<CircularProgress size={24} color="inherit" />) : (t("contentInput.buttons.search"))}
                                            </Button>
                                        </div>
                                    </>
                                )}
                            </div>
                        )
                    ) : (
                        <div className="file-preview-wrapper">
                            <div className="file-preview-container-inline">
                                {contentType === 'image' && imagePreview ? (
                                    <img
                                        src={imagePreview}
                                        alt="Preview"
                                        className="file-preview-icon image-preview-inline"
                                    />
                                ) : contentType === 'video' && contentPreviewUrl && isPlayableVideo ? (
                                    /* VIDEO PREVIEW LOGIC */
                                    <div
                                        className="video-preview-wrapper"
                                        onClick={(e) => e.stopPropagation()} // Prevent clicking video from opening file dialog
                                        onKeyDown={(e) => e.stopPropagation()}
                                    >
                                        <video
                                            src={contentPreviewUrl}
                                            controls
                                            className="video-preview-inline"
                                        >
                                            {t('contentInput.errors.browserVideoSupport')}
                                        </video>
                                    </div>
                                ) : contentType === 'audio' && contentPreviewUrl ? (
                                    <div
                                        className="audio-preview-wrapper"
                                        onClick={(e) => e.stopPropagation()}
                                        onKeyDown={(e) => e.stopPropagation()}
                                    >
                                        <audio src={contentPreviewUrl} controls className="audio-preview-inline">
                                            {t('contentInput.errors.browserAudioSupport')}
                                        </audio>
                                    </div>
                                ) : (
                                    fileTypeIcons[contentType]
                                )}
                                <span className="selected-filename">{uploadedFileName}</span>
                            </div>
                        </div>
                    )}
                </div>


                {/* --- CONTEXT ACCORDION --- */}
                {hasContent && renderContextAccordion()}

                <Tooltip title={tooltipTitle} arrow placement="top" disableHoverListener={!isButtonDisabled}>
                    <span style={{ display: "block", width: "100%" }}>
                        <button onClick={handleAnalyzeClick} disabled={isButtonDisabled} aria-label={t("contentInput.aria.analyzeFile", { translatedContentType })} className="analyze-button" style={isButtonDisabled ? { pointerEvents: "none" } : {}}>
                            {buttonText}
                        </button>
                    </span>
                </Tooltip>
            </div>
        )
    };

    // --- Determine Modal Text Dynamically ---
    const hasNote = contextUserNotes && contextUserNotes.trim().length > 0;
    const hasUrl = contextSourceUrl && contextSourceUrl.trim().length > 0;

    let warningDescKey = 'contentInput.context.publicWarningModal.description_note';
    let deleteBtnKey = 'contentInput.context.publicWarningModal.deleteNoteButton';

    if (hasNote && hasUrl) {
        warningDescKey = 'contentInput.context.publicWarningModal.description_both';
        deleteBtnKey = 'contentInput.context.publicWarningModal.deleteBothButton';
    } else if (hasUrl) {
        warningDescKey = 'contentInput.context.publicWarningModal.description_url';
        deleteBtnKey = 'contentInput.context.publicWarningModal.deleteUrlButton';
    }
    // ---------------------------------------------------

    return (
        <div className="content-input-main">
            {contentType === "text" ? (
                <div className="text-input-container">
                    <div className="text-upload-prompt">
                        {uploadedFileName ? (
                            <span className="selected-filename-text">File: {uploadedFileName}</span>
                        ) : (
                            <span></span>
                        )}
                        <div className="text-prompt-right-actions">
                            {/* Character Counter */}
                            <span
                                className="char-counter"
                                style={{
                                    // Use textDisplay.length here
                                    color: (textDisplay && textDisplay.length >= MAX_TEXT_LENGTH) ? '#ef5350' : 'rgba(255, 255, 255, 0.6)',
                                    marginRight: '15px'
                                }}
                            >
                                {/* Use textDisplay.length here as well */}
                                {textDisplay ? textDisplay.length : 0} / {MAX_TEXT_LENGTH}
                            </span>

                            <span>
                                {t("contentInput.dropZone.dragAndDrop", { translatedContentType: ".txt" })}&nbsp;
                                <span className="upload-link" onClick={() => textFileRef.current?.click()}>
                                    {t("contentInput.dropZone.uploadLink")}
                                </span>
                            </span>
                        </div>
                        <input
                            type="file"
                            ref={textFileRef}
                            onChange={handleFileChange}
                            accept=".txt"
                            style={{ display: 'none' }}
                        />
                    </div>
                    <div style={{ position: 'relative', width: '100%' }}>
                        {content && (
                            <Tooltip title={t("contentInput.tooltips.clearText")}>
                                <IconButton
                                    size="small"
                                    className="clear-text-button"
                                    onClick={handleClearContent}
                                >
                                    <CancelIcon fontSize="small" />
                                </IconButton>
                            </Tooltip>
                        )}
                        <textarea
                            value={textDisplay}
                            onChange={handleTextChange}
                            rows={6}
                            placeholder={t("contentInput.textPlaceholder")}
                            onDragEnter={handleDragEnter}
                            onDragOver={handleDragOver}
                            onDragLeave={handleDragLeave}
                            onDrop={handleTextDrop}
                            className={`${isDragging ? 'dragging' : ''} ${hasContent ? 'connected-bottom' : ''} main-analysis-textarea`}
                            maxLength={MAX_TEXT_LENGTH}
                        />
                    </div>

                    {/* --- URL Input for Text --- */}
                    {!content && (
                        <div style={{ marginBottom: '1rem', width: '100%' }}>
                            <div className="or-separator">{t("authPage.guest.separator")}</div>
                            
                            {isFetchingUrl ? (
                                <div className="url-fetching-state">
                                    <CircularProgress color="secondary" />
                                    {fetchMessage && (
                                        <Typography variant="body2" className="fetch-message-text">
                                            {fetchMessage}
                                        </Typography>
                                    )}
                                </div>
                            ) : (
                                <div className="url-input-container" onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()}>
                                    <TextField
                                        fullWidth variant="outlined"
                                        label={t("contentInput.dropZone.urlPlaceholder", { translatedContentType: ".txt" })}
                                        value={imageUrl} 
                                        onChange={(e) => setImageUrl(e.target.value)}
                                        disabled={isFetchingUrl || isLoading} 
                                        error={!!fetchError} 
                                        helperText={fetchError}
                                        InputProps={{ startAdornment: (<LinkIcon sx={{ color: "action.active", mr: 1, my: 0.5 }} />), }}
                                    />
                                    <Button 
                                        variant="contained" 
                                        onClick={handleFetchFileFromUrl} 
                                        disabled={isFetchingUrl || isLoading || !imageUrl} 
                                        className="search-button"
                                    >
                                        {isFetchingUrl ? (<CircularProgress size={24} color="inherit" />) : (t("contentInput.buttons.search"))}
                                    </Button>
                                </div>
                            )}
                        </div>
                    )}

                    {/* --- CONTEXT ACCORDION FOR TEXT --- */}
                    {hasContent && renderContextAccordion()}

                    <Tooltip title={tooltipTitle} arrow placement="top" disableHoverListener={!isButtonDisabled}>
                        <span style={{ display: "block", width: "100%" }}>
                            <button
                                onClick={handleAnalyzeClick}
                                disabled={isButtonDisabled}
                                className="analyze-button"
                                style={isButtonDisabled ? { pointerEvents: "none" } : {}}
                            >
                                {buttonText}
                            </button>
                        </span>
                    </Tooltip>
                </div>
            ) : (
                renderFileInput()
            )}

            {/* --- HISTORY MODAL --- */}
            <Dialog
                open={isHistoryModalOpen}
                onClose={() => setIsHistoryModalOpen(false)}
                PaperProps={{
                    sx: {
                        backgroundColor: '#ffffff !important', // White
                        color: 'rgba(0, 0, 0, 0.87) !important', // Dark text
                        minWidth: '500px',
                        border: 'none',
                        borderRadius: '12px',
                        boxShadow: '0 24px 38px 3px rgba(0,0,0,0.14) !important, 0 9px 46px 8px rgba(0,0,0,0.12) !important, 0 11px 15px -7px rgba(0,0,0,0.2) !important'
                    }
                }}
            >
                <DialogTitle sx={{ 
                    borderBottom: '1px solid rgba(0, 0, 0, 0.12) !important', 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: 1,
                    color: 'rgba(0, 0, 0, 0.87) !important' // Dark Title
                }}>
                    <HistoryIcon sx={{ color: 'var(--primary-color) !important' }} />
                    {t('contentInput.context.historyModalTitle')}
                </DialogTitle>
                <DialogContent sx={{ mt: 2, padding: '0 24px' }}>
                    <DialogContentText sx={{ color: 'rgba(0, 0, 0, 0.6) !important', mb: 2 }}>
                        {t('contentInput.context.historyModalDescription')}
                    </DialogContentText>
                    {contextHistory.length === 0 ? (
                        <Typography variant="body2" sx={{ fontStyle: 'italic', textAlign: 'center', py: 3, color: 'rgba(0, 0, 0, 0.6) !important' }}>
                            {t('contentInput.context.noHistory')}
                        </Typography>
                    ) : (
                        <List>
                            {contextHistory.map((item, index) => {
                                // Format Date (assuming item.analysis_timestamp exists from backend query)
                                const dateStr = item.analysis_timestamp
                                    ? new Date(item.analysis_timestamp).toLocaleString()
                                    : t('contentInput.context.historyLabels.na');

                                return (
                                    <ListItem key={index} disablePadding sx={{ borderBottom: '1px solid rgba(0,0,0,0.12) !important', mb: 1 }}>
                                        <ListItemButton
                                            onClick={() => applyContextFromHistory(item)}
                                            sx={{
                                                display: 'flex',
                                                flexDirection: 'column',
                                                alignItems: 'flex-start',
                                                gap: 0.5, // Tighter spacing
                                                py: 2,
                                                '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04) !important' }
                                            }}
                                        >
                                            {/* 1) Analyzed On */}
                                            <Typography variant="caption" sx={{ color: 'rgba(0, 0, 0, 0.6) !important', fontWeight: 'bold', mb: 0.5 }}>
                                                {t('contentInput.context.historyLabels.analyzedOn')}: {dateStr}
                                            </Typography>

                                            {/* 2) Source URL */}
                                            <Typography variant="body2" sx={{ color: 'rgba(0,0,0,0.87) !important' }}>
                                                <Box component="span" sx={{ color: 'rgba(0,0,0,0.6) !important', mr: 1 }}>
                                                    {t('contentInput.context.historyLabels.sourceUrl')}:
                                                </Box>
                                                <span style={{
                                                    fontFamily: 'monospace',
                                                    fontSize: '0.85rem',
                                                    wordBreak: 'break-all',
                                                    whiteSpace: 'normal',
                                                    color: 'var(--primary-color) !important'
                                                }}>
                                                    {item.source_url || t('contentInput.context.historyLabels.na')}
                                                </span>
                                            </Typography>

                                            {/* 3) Platform */}
                                            <Typography variant="body2" sx={{ color: 'rgba(0,0,0,0.87) !important' }}>
                                                <Box component="span" sx={{ color: 'rgba(0,0,0,0.6) !important', mr: 1 }}>
                                                    {t('contentInput.context.historyLabels.platform')}:
                                                </Box>
                                                {item.platform 
                                                    ? getPlatformDisplayName(item.platform, t) 
                                                    : t('contentInput.context.historyLabels.na')}
                                            </Typography>

                                            {/* 4) Additional Notes */}
                                            <Typography variant="body2" sx={{ color: 'rgba(0,0,0,0.87) !important' }}>
                                                <Box component="span" sx={{ color: 'rgba(0,0,0,0.6) !important', mr: 1 }}>
                                                    {t('contentInput.context.historyLabels.notes')}:
                                                </Box>
                                                <span style={{
                                                    fontStyle: item.user_notes ? 'italic' : 'normal',
                                                    fontSize: '0.85rem',
                                                    wordBreak: 'break-all',
                                                    whiteSpace: 'normal'
                                                }}>
                                                    {item.user_notes || t('contentInput.context.historyLabels.na')}
                                                </span>
                                            </Typography>

                                        </ListItemButton>
                                    </ListItem>
                                );
                            })}
                        </List>
                    )}
                </DialogContent>
                <DialogActions sx={{ borderTop: '1px solid rgba(255, 255, 255, 0.1)', p: 2 }}>
                    <Button onClick={() => setIsHistoryModalOpen(false)}
                        variant="outlined"
                        sx={{ 
                            // Target the root class to override global theme
                            '&.MuiButton-root': {
                                color: 'rgba(0, 0, 0, 0.6) !important', // Force Dark Text
                                borderColor: 'rgba(0, 0, 0, 0.23) !important', // Force Dark Border
                            },
                            '&.MuiButton-root:hover': { 
                                color: '#000000 !important', 
                                borderColor: '#000000 !important',
                                backgroundColor: 'rgba(0, 0, 0, 0.04) !important' 
                            } 
                        }}
                    >
                        {t('bulkAnalysisPage.dialogs.common.cancel')}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* --- CONSENT MODAL --- */}
            <Dialog
                open={isConsentModalOpen}
                onClose={() => setIsConsentModalOpen(false)}
                PaperProps={{ sx: { backgroundColor: '#2a1215', color: 'white', border: '1px solid #ef5350' } }}
            >
                <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, color: '#ef5350' }}>
                    <WarningAmberIcon /> {t('contentInput.context.publicWarningModal.title')}
                </DialogTitle>
                <DialogContent>
                    <DialogContentText sx={{ color: 'rgba(255,255,255,0.9)', mb: 2 }}>
                        {t(warningDescKey)}
                    </DialogContentText>
                    <DialogContentText sx={{ color: '#ffcdd2', fontWeight: 'bold', mb: 2 }}>
                        {t('contentInput.context.publicWarningModal.warning')}
                    </DialogContentText>
                    <DialogContentText sx={{ color: 'rgba(255,255,255,0.9)' }}>
                        {t('contentInput.context.publicWarningModal.question')}
                    </DialogContentText>

                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={dontShowConsentAgain}
                                onChange={(e) => setDontShowConsentAgain(e.target.checked)}
                                sx={{ color: 'rgba(255,255,255,0.5)', '&.Mui-checked': { color: '#ef5350' } }}
                            />
                        }
                        label={<Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)' }}>{t('contentInput.context.publicWarningModal.dontShowAgain')}</Typography>}
                        sx={{ mt: 2 }}
                    />
                </DialogContent>
                <DialogActions sx={{
                    flexDirection: 'column',
                    gap: 1,
                    alignItems: 'stretch',
                    px: 3,
                    pb: 3,
                    // This targets all children that are not the first child 
                    // and removes the default MUI left margin.
                    '& > :not(:first-of-type)': {
                        marginLeft: 0
                    }
                }}>
                    <Button
                        variant="contained"
                        color="error"
                        onClick={handleConsentConfirm}
                        fullWidth
                    >
                        {t('contentInput.context.publicWarningModal.agreeButton')}
                    </Button>
                    <Button
                        variant="outlined"
                        sx={{
                            borderColor: 'rgba(255, 255, 255, 0.5)',
                            color: 'rgba(255, 255, 255, 0.9)',
                            '&:hover': {
                                borderColor: 'var(--secondary-color)',
                                backgroundColor: 'rgba(79, 195, 247, 0.1)',
                                color: 'var(--secondary-color) !important',
                            }
                        }}
                        onClick={handleDeleteNoteAndProceed}
                        fullWidth
                    >
                        {t(deleteBtnKey)}
                    </Button>
                    <Button
                        onClick={() => setIsConsentModalOpen(false)}
                        sx={{ color: 'rgba(255,255,255,0.5)' }}
                        fullWidth // Added this prop
                    >
                        {t('contentInput.context.publicWarningModal.cancelButton')}
                    </Button>
                </DialogActions>
            </Dialog>
        </div >
    );
}

export default ContentInput;